import { startWith, takeUntil, tap } from 'rxjs/operators';
import {
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { combineLatest, fromEvent, merge, Observable, Subject } from 'rxjs';

import { GasDynamicComponentsService } from '@cikrf/gas-utils/services';

import { GasPositioningPopup } from './gas-dynamic-positioning-popup.namespace';
import { GasPositioning } from '../../utils/gas-positioning/gas-positioning.namespace';
import { GasPositioningService } from '../../utils/gas-positioning/gas-positioning.service';
import { GasBrowserEventsService } from '../../utils/gas-browser-event/gas-browser-events.service';
import { GasDynamicBaseComponent } from '../../core/gas-dynamic-base/gas-dynamic-base.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/**
 * Данная директива призвана для создания динамических компонентов
 * Которые будут располагаться в зависимости от конфигурации
 *
 * @generic T указывает тип возвращаемых данных при закрытии popup.
 * @generic U указывает тип props используемого компонента
 */
@UntilDestroy()
@Directive({
  selector: '[gasDynamicPositioningPopup]',
})
export class GasDynamicPositioningPopupDirective<T, U> implements OnDestroy, OnInit {
  @Input('gasDynamicPositioningPopup')
  public config: GasPositioningPopup.Config<U>;

  // todo: проверить где используется и удалить т.к. перенесено в config
  @Input()
  public nativeDisabled = false;

  @Output('submited')
  public submited$ = new EventEmitter();

  @Output('opened')
  public opened$ = new EventEmitter<boolean>();

  public clickedOutside$ = new EventEmitter();

  public destroyed$ = new Subject<void>();

  private componentRef: ComponentRef<GasDynamicBaseComponent<T>>;

  private browserScreenEvents$: Observable<Event[]>;

  constructor(
    private el: ElementRef,
    private gasDynamicComponentsService: GasDynamicComponentsService,
    private gasPositioningService: GasPositioningService,
    private gasBrowserEventsService: GasBrowserEventsService,
    private render: Renderer2,
  ) {}

  @HostListener('click', ['$event'])
  public clickByDirective(event: MouseEvent): void {
    if (!this.componentRef || !this.config) {
      this.open();

      return;
    }

    if (event?.target === this.el.nativeElement && !this.config?.isHoverable) {
      this.destroy();
    }
  }

  // todo: надо последить что это изменение ничего не сломало
  // было так: @HostListener('document:keydown', ['$event'])
  @HostListener('keydown', ['$event'])
  public keydownHandler(event: KeyboardEvent): void {
    if (event.key === GasPositioningPopup.ESCAPE_KEY) {
      this.destroy();
    }

    if (!this.componentRef && this.config.isInputOpen) {
      this.open();
    }
  }

  public ngOnInit(): void {
    if (this.config.isHoverable) {
      const element: HTMLElement = this.el.nativeElement;

      element.onmouseenter = () => this.onMouseEnter();
      element.onmouseleave = () => this.onMouseLeave();
    }

    if (this.config.closeSubject$) {
      this.config.closeSubject$
        .pipe(
          untilDestroyed(this),
        )
        .subscribe(() => this.destroy());
    }
  }

  public ngOnDestroy(): void {
    this.destroy();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private onMouseEnter(): void {
    if (this.config?.isHoverable) {
      this.clickByDirective(null);
    }

    this.componentRef?.instance?.hoveredMainElement$.next(true);
  }

  private onMouseLeave(): void {
    if (this.config?.isHoverable) {
      this.componentRef?.instance?.hoveredMainElement$.next(false);
    }
  }

  private open(): void {
    if (this.nativeDisabled || this.config.disabled) {
      return;
    }

    this.componentRef = this.gasDynamicComponentsService.createComponent(
      this.config.component,
    );

    const popupNativeElement: HTMLElement = this.componentRef.location.nativeElement;

    this.setStyles(popupNativeElement);
    this.setProps();
    this.opened$.emit(true);

    this.componentRef.instance.render$
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.subscribeIntersection(popupNativeElement);

        /** Подписываемся на изменение скролла и resize окна */
        const observables$: Observable<Event>[] = [
          this.gasBrowserEventsService.resize$.pipe(startWith<Event, Event>(null)),
          this.gasBrowserEventsService.scroll$.pipe(startWith<Event, Event>(null)),
        ];

        /** В случае, если у вас какой то кастомный scroll, то и на него подписываемся */
        if (this.config.scrollElement) {
          observables$.push(fromEvent(this.config.scrollElement(), 'scroll').pipe(startWith<Event, Event>(null)));
        }

        this.browserScreenEvents$ = combineLatest(observables$);

        /** Закрытие по клику вне элемента, при режиме открытия popup по клику */
        if (!this.config?.isHoverable && this.config.isCloseByClickOutside) {
          this.componentRef.instance.clickOutside$
            .pipe(
              takeUntil(this.destroyed$),
            )
            .subscribe(() => this.destroy());
        }

        /** Уничтожение popup, при режиме показа popup при hover */
        if (this.config?.isHoverable) {
          this.componentRef.instance.hovered$
            .pipe(
              takeUntil(this.destroyed$),
            )
            .subscribe((isHovered: boolean) => {
              if (!isHovered) {
                this.destroy();
              }
            });
        }

        /** Устанавливаем позиционирование при scrolling, ресайзе и клике */
        merge(
          this.browserScreenEvents$,
          // TODO: разобраться, действительно ли надо рендерить по каждому клику
          // fromEvent(this.componentRef.location.nativeElement, 'click'),
        )
          .pipe(
            takeUntil(this.destroyed$),
          )
          .subscribe(() => this.setPosition(popupNativeElement));
      });

    this.componentRef.instance.closeComponent$
      .pipe(
        tap((submitedData: T) => {
          if (submitedData) {
            this.submited$.emit(submitedData);
          }
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => this.destroy());
  }

  private destroy(): void {
    if (this.componentRef) {
      this.clearStyles();

      this.gasDynamicComponentsService.deleteComponent(this.componentRef);

      this.componentRef = undefined;

      this.opened$.emit(false);
    }
  }

  /** Intersection смотрит на то, что данный элемент пропал из зоны видимости, в этом случае убиваем popup */
  private subscribeIntersection(popupNativeElement: HTMLElement): void {
    if (IntersectionObserver) {
      const options: IntersectionObserverInit = {};

      const popupObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
        if (!entries[0].isIntersecting) {
          popupObserver.unobserve(popupNativeElement);
          this.destroy();
        }
      }, options);

      popupObserver.observe(popupNativeElement);
    }
  }

  /** Установка позиции */
  private setPosition(popupNativeElement: HTMLElement): void {
    const isElementsExists: boolean = this.componentRef && this.componentRef.location.nativeElement;

    if (isElementsExists) {
      this.gasPositioningService.positioning({
        offset: this.config.offset || 0,
        preferablePositions: this.config.preferablePositions,
        arrowWidth: this.config.arrowWidth || 0,
        popup: this.componentRef.location.nativeElement,
        parent: this.el.nativeElement,
        alignType: this.config.alignType || 'auto',
        after: (side: GasPositioning.Direction) => {
          this.componentRef.instance.alignedPosition = side;
        },
      });
    }

    this.render.setStyle(popupNativeElement, 'visibility', 'visible');
  }

  /** Проксирование необходимых инпутов, берем из свойства props в конфиге */
  private setProps(): void {
    if (this.config.hasOwnProperty('props')) {
      Object.keys(this.config.props)
        .forEach((key: string) => this.componentRef.instance[key] = this.config.props[key]);
    }
  }

  private setStyles(popupNativeElement: HTMLElement): void {
    this.el.nativeElement.style.position = 'relative';

    popupNativeElement.style.position = 'fixed';

    const className = !this.config.className || typeof this.config.className !== 'string' ? 'p-popup' : this.config.className;

    this.render.addClass(popupNativeElement, className);
    this.render.addClass(this.el.nativeElement, className);
    this.render.setStyle(popupNativeElement, 'visibility', 'hidden');
  }

  private clearStyles(): void {
    const className = !this.config.className || typeof this.config.className !== 'string' ? 'p-popup' : this.config.className;

    this.render.removeClass(this.el.nativeElement, className);
  }
}
